import React, {useCallback} from "react";
import {gql} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {InviteParticipants} from "../../../scenes/account/members";
import {Project, EducationMemberRow, Challenge, User} from "../../../schema";
import {useModal} from "../../ModalProvider";

import EditItemDropdown from "../../EditItemDropdown";
import AssignModal from "../AssignModal";

type PickedChallenge = Pick<Challenge, "id" | "name">;
type PickedProject = Pick<Project, "id" | "name"> & {
  challenges: Pick<Challenge, "id" | "name">[];
};

type props = {
  member: Pick<EducationMemberRow, "user">;
  challenges?: PickedChallenge[];
  projects?: PickedProject[];
  onAssign?: InviteParticipants;
  onSetStatus?: (userId: string, isActive: boolean) => void;
  onUserBestAttemptsReportExport?: (user: Pick<User, "id" | "fullName" | "email">) => void;
};

export default function MemberEditDropdown({
  member,
  challenges,
  projects,
  onAssign,
  onSetStatus,
  onUserBestAttemptsReportExport,
}: props) {
  const {t} = useTranslation();
  let {user} = member;

  const {add: addModal} = useModal();

  const onAssignButtonClick = useCallback(
    (e: React.MouseEvent) => {
      const assignToUser: InviteParticipants = (challenges, usersIds, emails, adminText) => {
        onAssign && onAssign(challenges, [user.id], undefined, adminText);
        modal.remove();
      };

      const modal = addModal({
        header: <AssignModal.Header />,
        content: (
          <AssignModal.Content
            challenges={challenges}
            projects={projects}
            user={user.id}
            params={{onAssign: assignToUser}}
            isSingleAssignment={true}
          />
        ),
      });
    },
    [challenges, projects, onAssign, user.id, addModal],
  );

  const toggleStatus = useCallback(() => {
    onSetStatus && onSetStatus(user.id, !user.hasActiveMembership);
  }, [onSetStatus, user]);

  const handleDownloadPdf = useCallback(() => {
    onUserBestAttemptsReportExport && onUserBestAttemptsReportExport(user);
  }, [onUserBestAttemptsReportExport, user]);

  return (
    <EditItemDropdown>
      {user.hasActiveMembership && (
        <EditItemDropdown.Action title={t("components.MemberEditDropdown.assign")} onClick={onAssignButtonClick} />
      )}
      <EditItemDropdown.Action
        title={
          user.hasActiveMembership
            ? t("components.MemberEditDropdown.deactivate")
            : t("components.MemberEditDropdown.activate")
        }
        onClick={toggleStatus}
      />
      <EditItemDropdown.Action
        title={t("components.MemberEditDropdown.downloadPdf")}
        onClick={handleDownloadPdf}
        disabled={!onUserBestAttemptsReportExport}
      />
    </EditItemDropdown>
  );
}

MemberEditDropdown.fragments = {
  root: gql`
    fragment MemberEditDropdown on EducationMemberRow {
      user {
        id
        name
        fullName
        email
        hasActiveMembership
      }
    }
  `,
  challenges: gql`
    fragment MemberEditDropdownChallenges on Challenge {
      ...AssignModalChallenges
    }
    ${AssignModal.fragments.challenges}
  `,
  projects: gql`
    fragment MemberEditDropdownProjects on Project {
      ...AssignModalProjects
    }
    ${AssignModal.fragments.projects}
  `,
};
